import { useState, useRef } from 'react';
import { uploadCustomizationImage } from '../../../lib/customization/customizationService';

interface UseImageUploadProps {
    storeId: string;
    setNotification: (notif: { message: string | null; type: 'success' | 'error' }) => void;
    onImageUpdate: (field: 'desktopSrc' | 'mobileSrc', url: string) => void;
}

export const useImageUpload = ({ storeId, setNotification, onImageUpdate }: UseImageUploadProps) => {
    const [uploadingDesktop, setUploadingDesktop] = useState(false);
    const [uploadingMobile, setUploadingMobile] = useState(false);
    const uploadingRef = useRef(false);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'desktopSrc' | 'mobileSrc') => {
        if (uploadingRef.current) return;
        
        const inputTarget = e.target; 
        const file = inputTarget.files?.[0];
        if (!file) return;

        const MAX_FILE_SIZE = 1024 * 1024; // 1MB

        if (file.size > MAX_FILE_SIZE) {
            setNotification({
                message: `File size exceeds ${MAX_FILE_SIZE / 1024 / 1024} MB.`,
                type: "error",
            });
            return;
        }

        uploadingRef.current = true;
        if (field === 'desktopSrc') setUploadingDesktop(true);
        else setUploadingMobile(true);

        const clean = file.name.replace(/[^\w.-]/g, "_");
        const sanitized = new File([file], clean, { type: file.type });

        try {
            const url = await uploadCustomizationImage(storeId, sanitized);

            onImageUpdate(field, url);

        } catch (error: any) {
            console.error('Error uploading image:', error);
            setNotification({ 
                message: "Image upload failed. Please try again.", 
                type: 'error' 
            });
        } finally {
            uploadingRef.current = false;
            if (field === 'desktopSrc') setUploadingDesktop(false);
            else setUploadingMobile(false);
            
            inputTarget.value = ''; 
        }
    };

    const handleRemoveImage = (field: 'desktopSrc' | 'mobileSrc') => {
        onImageUpdate(field, '');
    };

    return {
        uploadingDesktop,
        uploadingMobile,
        handleImageUpload,
        handleRemoveImage
    };
};